import { useState } from 'react';
import { FieldErrors, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/components/ui/helpers/toast';
import { useShallow } from 'zustand/react/shallow';

import {
  ANALYTICS_EVENT,
  ANALYTICS_FLOW,
} from '@/lib/analytics/types/analytics-enums';
import { useAnalytics } from '@/hooks/analytics/use-analytics';
import { trackSuccessfulSignup } from '@/lib/analytics/track-signup';
import { useAuthSignup } from '@/features/auth/hooks/use-auth-signup';
import * as Logger from '@/lib/monitoring/logger';
import { getApiErrorMessage } from '@/lib/helpers/api-errors/get-api-error-message';
import { useSignupFlowStore } from '@/features/signup/stores/use-signup-flow-store';
import type {
  RegistrationFormData,
  UseRegistrationFormReturn,
} from '../types/use-registration-form-types';
import { EMAIL_STATUS } from '../types/use-registration-form-enums';


export function useRegistrationForm(): UseRegistrationFormReturn {
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const { mutateAsync: signupMutate, isLoading } = useAuthSignup();

  const { setEmail, setPassword, setCompletedStep } = useSignupFlowStore(
    useShallow(s => ({
      setEmail: s.setEmail,
      setPassword: s.setPassword,
      setCompletedStep: s.setCompletedStep,
    }))
  );

  const [submitError, setSubmitError] = useState<string>();
  const [emailStatus, setEmailStatus] = useState<EMAIL_STATUS>(EMAIL_STATUS.IDLE);

  const form = useForm<RegistrationFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
    },
    mode: 'onSubmit',
  });

  const emailAvailable = emailStatus !== EMAIL_STATUS.TAKEN && emailStatus !== EMAIL_STATUS.CHECKING;
  const canSubmit = emailAvailable && !isLoading;

  const onSubmit = async (data: RegistrationFormData) => {
    setSubmitError(undefined);

    try {
      const response = await signupMutate({
        email: data.email.trim().toLowerCase(),
        password: data.password,
        givenName: data.firstName.trim(),
        familyName: data.lastName.trim(),
      });

      if (response?.data) {
        const { user } = response.data;

        setEmail(user.email);
        setPassword(data.password);
        setCompletedStep(1);

        trackSuccessfulSignup({
          email: user.email,
          firstName: data.firstName.trim(),
          lastName: data.lastName.trim(),
          flow: ANALYTICS_FLOW.SIGNUP_V2,
        });

        toast.success('Account created! Please verify your email.');

        void navigate('/signup/otp');
      }
    } catch (err) {
      Logger.error('Signup Flow V2 - User creation failed', err instanceof Error ? err : null, { error: err });
      const errorMessage = getApiErrorMessage(err);
      toast.error(errorMessage);
      setSubmitError(errorMessage);

      track(ANALYTICS_EVENT.SIGNUP_USER_CREATION_FAILED, {
        email: data.email,
        error: errorMessage,
      });
    }
  };

  const focusFirstInvalidField = (fieldErrors: FieldErrors<RegistrationFormData>) => {
    const firstErrorField = Object.keys(fieldErrors)[0];
    if (!firstErrorField) {
      return;
    }

    const fieldElement = document.getElementById(firstErrorField);
    fieldElement?.focus();
  };

  const handleSubmit = (e?: React.BaseSyntheticEvent) => {
    void form.handleSubmit(onSubmit, focusFirstInvalidField)(e);
  };

  return {
    form,
    emailStatus,
    setEmailStatus,
    isSubmitting: isLoading,
    submitError,
    canSubmit,
    handleSubmit,
  };
}
